// White-space as thousand separator seems to be a good generic way to avoid confusion
// between dots and commas as been investigated previously. In other words, meaning ,
// and.can be confused with each other regardless of which way it's "wrong" since they're
// both used as decimal, but since white - space isn't it's safe to use - especially
// since a reader can simply ignore them(which is very easy with blank space).

import { parseISO, format } from 'date-fns';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

function adjustTimezone(date, remove) {
  let timezoneAdjustment = date.getTimezoneOffset() * 60 * 1000;

  if (remove) timezoneAdjustment = -timezoneAdjustment;

  return new Date(date.valueOf() + timezoneAdjustment);
}

// For calendar dates the user picked (deadlines, drops, seasons): shown as-is, with no timezone conversion.
export function dateFormatter(date, _format) {
  return format(parseISO(date), _format);
}

export function dateToServerDateString(date) {
  return removeTimezone(date).toISOString();
}

export function numberFormatter(
  value,
  { fallback, thousandSeparator, maximumFractionDigits } = {
    fallback: null,
    thousandSeparator: ' ',
    maximumFractionDigits,
  }
) {
  const ret = new Intl.NumberFormat('en-US', { maximumFractionDigits }).format(value).replace(',', thousandSeparator);

  if (ret.includes('NaN')) return fallback;

  return ret;
}

// TODO: Maybe we should use some kind of generic lib for this? Or Intl.NumberFormat (see e.g. https://stackoverflow.com/questions/29255843/is-there-a-way-to-reverse-the-formatting-by-intl-numberformat-in-javascript )
export function parseStringToNumber(value, fallback = null) {
  // Already number
  if (isNumber(value) && !isNaN(value)) return value;

  // Only handle strings (in any form)
  if (!isString(value)) return fallback;

  // Handle both commas and dots
  const dotExists = value.indexOf('.') > -1;
  const commaExists = value.indexOf(',') > -1;

  // If we only have commas, then let's assume they should be treated as dots
  if (commaExists && !dotExists) value = value.replaceAll(',', '.');

  // If we use space as separator (e.g. thousand separator), remove them since they'll mess parsing (and aren't adding anything to the parsing process)
  value = value.replaceAll(' ', '');

  const result = parseFloat(value); // Compared to Number constructor, this one filters out possible other alphabetical, irrelevant characters, see e.g. https://gomakethings.com/converting-strings-to-numbers-with-vanilla-javascript/#parsefloat

  if (result !== 0 && !result) return fallback; // Not zero, but another falsy value (e.g. NaN)

  return result;
}

export function removeTimezone(date) {
  return adjustTimezone(date, true);
}

export function dateToDateOnlyString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function parseDateOnlyString(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function serverDateStringToDate(dateString) {
  return parseISO(dateString);
}

// Backend DateTimes are serialised without a timezone offset but represent UTC.
// parseISO would treat them as local time, so we force a 'Z' when missing.
export function parseServerUtcDate(dateString) {
  return parseISO(/([+-]\d\d:?\d\d|Z)$/.test(dateString) ? dateString : `${dateString}Z`);
}

// For moments the server recorded (createdAt, updatedAt, uploads): converted from UTC to the user's local time.
export function serverTimestampFormatter(dateString, _format) {
  return format(parseServerUtcDate(dateString), _format);
}

// Filter out non-numeric characters from a string (useful for OTP codes, etc.)
export function filterNumericOnly(value) {
  if (!value || typeof value !== 'string') return value;
  return value.replace(/\D/g, '');
}
